import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const DESIGNS = [
  {
    key: "monogram",
    name: "Monogramme M·S·B",
    tagline: "Éditorial, intemporel",
    description: "Un monogramme typographique italique enserré dans un cercle gravé. Référence aux maisons d'édition, aux hôtels particuliers, à la papeterie raffinée. Le plus chic et le plus discret.",
    pairings: ["Pages d'accueil épurées", "Papier à en-tête", "Tampon de signature"],
  },
  {
    key: "lighthouse",
    name: "Phare",
    tagline: "Iconique, nautique",
    description: "Un phare géométrique simplifié, signe maritime universellement breton. Le faisceau de lumière ocre apporte une touche chaleureuse. Évocateur, immédiatement reconnaissable.",
    pairings: ["Marketing visuel fort", "Cartes postales", "Goodies"],
  },
  {
    key: "wave",
    name: "Soleil & Vague",
    tagline: "Contemporain, slow travel",
    description: "Un demi-soleil ocre se levant au-dessus d'une vague stylisée. Minimalisme inspiré des affiches japonaises et de l'édition Art Print. Apaisant, moderne, intemporel.",
    pairings: ["Réseaux sociaux", "Mood premium", "Hôtellerie de charme"],
  },
  {
    key: "ermine",
    name: "Moucheture d'hermine",
    tagline: "Héraldique, signature",
    description: "La moucheture d'hermine — symbole le plus identitairement breton — au cœur d'un blason aux formes adoucies. Une signature locale forte sans aucune référence folklorique.",
    pairings: ["Authenticité premium", "Coffret cadeau", "Badge fournisseur"],
  },
];

const STORAGE_KEY = "msb_logo_design";

export default function LogoPreviewPage() {
  const [selected, setSelected] = useState(() => localStorage.getItem(STORAGE_KEY) || "lighthouse");

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, selected);
  }, [selected]);

  const adopt = (key) => {
    setSelected(key);
    localStorage.setItem(STORAGE_KEY, key);
    window.dispatchEvent(new CustomEvent("msb-logo-design", { detail: key }));
    toast.success(`Logo "${DESIGNS.find(d => d.key === key)?.name}" adopté · appliqué partout`);
  };

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-600 hover:text-[#0A0A0A] mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
        </Link>
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">
          Identité visuelle
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          Choisissez votre <em className="italic font-light">logo</em>
        </h1>
        <p className="mt-4 text-neutral-600 max-w-2xl leading-relaxed">
          Quatre directions vraiment distinctes, toutes chic et modernes. Cliquez sur celle qui parle le mieux à votre Bretagne — le logo sera appliqué partout (header, footer, factures, emails).
        </p>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {DESIGNS.map((d) => {
            const isSelected = d.key === selected;
            return (
              <div
                key={d.key}
                data-testid={`logo-option-${d.key}`}
                className={`border-2 bg-white p-8 transition-all ${
                  isSelected ? "border-[#0A0A0A] shadow-2xl" : "border-black/10 hover:border-black/40"
                }`}
              >
                {/* Light preview */}
                <div className="bg-[#FAFAFA] border border-black/10 p-8 flex items-center justify-center min-h-[180px]">
                  <Logo design={d.key} size={72} />
                </div>
                {/* Dark preview */}
                <div className="bg-[#0A0A0A] border border-black/10 border-t-0 p-8 flex items-center justify-center min-h-[180px]">
                  <Logo design={d.key} theme="light" size={72} />
                </div>

                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-1">
                      Option {DESIGNS.indexOf(d) + 1} · {d.tagline}
                    </div>
                    <div className="font-display text-2xl font-bold">{d.name}</div>
                  </div>
                  {isSelected && (
                    <span className="shrink-0 bg-[#0A0A0A] text-white px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1.5">
                      <Check className="w-3 h-3" /> Choisi
                    </span>
                  )}
                </div>

                <p className="mt-4 text-sm text-neutral-600 leading-relaxed">{d.description}</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {d.pairings.map((p) => (
                    <span key={p} className="text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 bg-[#F5F2EC] text-neutral-700">
                      {p}
                    </span>
                  ))}
                </div>

                <button
                  data-testid={`adopt-${d.key}`}
                  onClick={() => adopt(d.key)}
                  disabled={isSelected}
                  className={`mt-6 w-full py-3 font-semibold text-sm transition-colors ${
                    isSelected
                      ? "bg-neutral-100 text-neutral-400 cursor-default"
                      : "bg-[#0A0A0A] text-white hover:bg-[#1B2A49]"
                  }`}
                >
                  {isSelected ? "Logo actuel" : `Adopter ce logo`}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-16 p-8 bg-white border border-black/10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">
            Aperçu en contexte
          </div>
          <h3 className="font-display text-2xl font-bold mb-6">Header avec le logo sélectionné</h3>
          <div className="border border-black/10 bg-white p-6">
            <Logo design={selected} size={48} />
          </div>
          <div className="border border-black/10 border-t-0 bg-[#0A0A0A] p-6">
            <Logo design={selected} theme="light" size={48} />
          </div>
          <p className="mt-4 text-xs text-neutral-500 italic">
            Astuce : le logo se met à jour automatiquement après rafraîchissement de la page. Vous pouvez changer d'avis à tout moment ici.
          </p>
        </div>
      </div>
    </div>
  );
}
